import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsI18n } from './i18n';
import { getNoClientsPaged } from '../../services/noClientsService';
import './noClients.scss';
import usePermissionsAI from 'permissions/modules/artificialIntelligence/usePermissionsAI';

const LostDealsList = ({ DesignSystemComponents }) => {
  const { Card, Table, IconButton, Input } = DesignSystemComponents || {};

  const navigate = useNavigate();
  const { t } = useScopedTranslation(noClientsI18n);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const { canViewNoClientesCall } = usePermissionsAI();

  const handleViewAnalysis = useCallback(
    (row) => {
      const params = new URLSearchParams({
        id: String(row?.callTranscriptId || ''),
        lostSalesAudioId: row?.lostSalesAudioId || '',
        dealName: row?.dealName || '',
        hubspotOwnerName: row?.hubspotOwnerName || '',
      });

      navigate(`/ia/no-clientes/analisis-protocolo?${params}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      // { accessor: 'dealId', label: 'ID', sort: true },
      { accessor: 'dealName', label: t('nc_col_name'), sort: true },
      {
        accessor: 'phone',
        label: t('nc_col_phone'),
        sort: true,
        render: (row) => row.phone || '--',
      },
      {
        accessor: 'hubspotOwnerName',
        label: t('nc_col_owner'),
        sort: true,
      },
      {
        accessor: 'closeDate',
        label: t('nc_col_closeDate'),
        sort: true,
        render: (row) =>
          row.closeDate
            ? new Date(row.closeDate).toLocaleDateString(undefined, {
                timeZone: 'UTC',
              })
            : '--',
      },
      // {
      //   accessor: 'dealStage',
      //   label: 'Etapa',
      //   sort: true,
      // },
      {
        accessor: 'lostReason',
        label: t('nc_col_lostReason'),
        sort: true,
        render: (row) => row.lostReason || '--',
      },
      {
        label: t('nc_col_actions'),
        align: 'right',
        render: (row) =>
          row?.dealId ? (
            <div className='d-flex '>
              <IconButton
                ariaLabel={t('nc_aria_viewAnalysis')}
                icon='eye'
                size='md'
                iconStroke='var(--primary-500)'
                onClick={() => handleViewAnalysis(row)}
                disabled={!canViewNoClientesCall}
              />
            </div>
          ) : (
            <span className='color-gray-600'>--</span>
          ),
      },
    ],
    [IconButton, handleViewAnalysis, canViewNoClientesCall, t]
  );

  const handleServerSortChange = ({ sortKey: key, direction } = {}) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextSearch = searchDraft.trim();
      setSearch((prev) => {
        if (prev === nextSearch) return prev;
        setPage(0);
        return nextSearch;
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchDraft]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchLostDeals = async () => {
      try {
        setLoading(true);

        const response = await getNoClientsPaged({
          page: page + 1,
          pageSize,
          sortBy: sortKey,
          desc: sortDirection ? sortDirection === 'desc' : null,
          search: search || null,
          signal: controller.signal,
        });

        setRows(Array.isArray(response?.data) ? response.data : []);
        setTotalItems(Number(response?.total) || 0);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading lost deals:', error);
          setRows([]);
          setTotalItems(0);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLostDeals();
    return () => controller.abort();
  }, [page, pageSize, sortKey, sortDirection, search]);

  return (
    <div className='no-clients-page'>
      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('nc_pageTitle')}</h2>
        <h4 className='fw-normal'>{t('nc_pageSubtitle')}</h4>
      </div>

      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='mb-3'>
          <Input
            icon='search'
            name='searchNoClients'
            placeholder={t('nc_searchPlaceholder')}
            type='text'
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </div>
        <div className='table-container'>
          <Table
            columns={columns}
            data={rows}
            loading={loading}
            paginator
            paginationMode='server'
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            totalItems={totalItems}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default LostDealsList;
